import { useCallback, useMemo } from 'react';
import {
  applyElementCategory,
  canChangeElementCategory,
  getSubCategoryConversionFactors,
  getSumOfCategoryConversionFactors,
} from '@shared/helpers/element-category.helpers';
import {
  ElementCategoryID,
  MainElementCategoryConversionFactorRecord,
} from '@shared/models/element-categories.model';
import {
  ElementKind,
  IElement,
  OneOfChildElements,
  Project,
} from '@shared/models/project.model';
import { getProject, useUpdateProject } from '@client/store/project';
import { updateElements } from '@shared/helpers/project.helpers';
import { getSelectedVersion } from '@client/store/ui';
import { useResolvedVersion } from './use-resolved-version.hooks';
import { enrichElementStructure } from '@shared/helpers/project.helpers';
import {
  getElementById,
  getPathToElement,
} from '@shared/helpers/recursive-element.helpers';
import { getProductsLookup } from '@client/store/product';
import { getDataTables } from '@features/data-table/store/data-table-store.hooks';
import { setElementExpanded } from './use-expand-elements.hooks';
import { useApplyFallbackName } from './use-element-name.hooks';
import amplitudeLog from '@client/amplitude';
import { useVersionExpressionVariablesRecord } from './use-element-expression-variables.hooks';
import { SupportedConversionUnits } from '@shared/helpers/conversion-factors.helpers';
import { useNewContentConfirmation } from './use-new-content-confirmation.hooks';
import { EMPTY_FROZEN_ARRAY } from '@shared/helpers/array.helpers';
import { AMP_ELEMENT_CATEGORY_SELECT } from '@shared/constants';

/**
 * @returns
 * The children of the selected main category element
 * or children of all main category elements if none is selected.
 * If main categories are missing, the children of building version are returned.
 * When a proposal is selected, reflects {@link useResolvedVersion} (proposal changes applied).
 */
export const useRootElementChildren = (): OneOfChildElements[] => {
  const version = useResolvedVersion();

  // Remove static empty array when version is undefined to avoid rerenders
  return version?.elements ?? EMPTY_FROZEN_ARRAY;
};

export const useMaxValueOfCategoryConversionFactors = (
  record: MainElementCategoryConversionFactorRecord,
  factor: SupportedConversionUnits,
): number =>
  useMemo(
    () =>
      Math.max(
        ...getSubCategoryConversionFactors(record).map((factors) =>
          getSumOfCategoryConversionFactors(factors, factor),
        ),
      ),
    [factor, record],
  );

/**
 * Apply element category to an element and update the selected recipe accordingly
 * @returns
 */
export const useApplyElementCategory = (): ((
  element: IElement,
  id?: ElementCategoryID,
) => Promise<Project>) => {
  const updateProject = useUpdateProject();
  const applyFallbackName = useApplyFallbackName();
  const variablesRecord = useVersionExpressionVariablesRecord();

  return useCallback(
    async (element: IElement, id?: ElementCategoryID) => {
      element = applyFallbackName(applyElementCategory(element, id));

      const path = getPathToElement(getSelectedVersion(true), element);

      element = enrichElementStructure(
        element,
        path,
        variablesRecord,
        getProductsLookup(),
        getDataTables(),
      );

      const updatedProject = updateElements(getProject(), element);
      // TODO(replay): express as ProjectChange so a 422 race can be replayed.
      return updateProject({ kind: 'snapshot', project: updatedProject });
    },
    [applyFallbackName, updateProject, variablesRecord],
  );
};

export const useSelectElementCategory = (element: IElement | undefined) => {
  const applyElementCategory = useApplyElementCategory();
  const confirmCategory = useNewContentConfirmation();

  return useCallback(
    async (id: ElementCategoryID): Promise<'selected' | 'cancelled'> => {
      if (!element || !canChangeElementCategory(element)) return 'cancelled';
      if (!(await confirmCategory(element, id))) return 'cancelled';

      const updatedProject = await applyElementCategory(element, id);

      // Element will be a new instance so get it from project
      const updatedElement = getElementById(
        updatedProject,
        element.id,
        ElementKind.Element,
      );

      if (
        updatedElement.elements.length &&
        updatedElement.recipe_id?.includes('auto')
      ) {
        setElementExpanded(updatedElement.id, true);
      }

      amplitudeLog(AMP_ELEMENT_CATEGORY_SELECT, { CategoryId: id });

      return 'selected';
    },
    [applyElementCategory, confirmCategory, element],
  );
};
